import dayjs from 'dayjs';
import { isNonEmploymentTypeValue } from '@/shared/employee';
import { DATE_FORMATS } from '@/utils/formUtils';
import {
  formatSpan,
  workExperienceTotal,
} from '../../_components/form/workExperience';

const fmt = (value) =>
  value ? dayjs(value).format(DATE_FORMATS.DISPLAY) : null;

const filled = (v) => v !== undefined && v !== null && String(v).trim() !== '';

// Personal-file fields counted on the "Personal details" tile and in the
// dossier completeness chart.
export const DOSSIER_FIELDS = [
  ['generalData', 'email'],
  ['generalData', 'phone'],
  ['generalData', 'address'],
  ['generalData', 'city'],
  ['generalData', 'municipality'],
  ['generalData', 'country'],
  ['additionalPersonalInfo', 'birthDate'],
  ['additionalPersonalInfo', 'placeOfBirth'],
  ['additionalPersonalInfo', 'gender'],
  ['additionalPersonalInfo', 'familyCelebrationDate'],
  ['additionalPersonalInfo', 'maritalStatus'],
  ['additionalPersonalInfo', 'educationLevel'],
];

// Heads of the employee's sector, as the server sends them; '' for none.
export const supervisorNames = (employee) =>
  (employee?.supervisors || [])
    .map((s) => s?.fullName)
    .filter(Boolean)
    .join(', ');

export const getIn = (obj, path) =>
  path.reduce((acc, key) => (acc == null ? acc : acc[key]), obj);

export const countDossier = (employee) => {
  const done = DOSSIER_FIELDS.filter((p) => filled(getIn(employee, p))).length;
  return { done, total: DOSSIER_FIELDS.length };
};

/**
 * Which tiles exist for this employee — the same conditions the old edit page
 * used to show the matching blocks — grouped in display order.
 */
export function buildTileGroups({
  employee,
  isHolidayTracker,
  showTeam,
  showSalary,
  showEquipment = false,
  showZaz = false,
}) {
  const employmentType = employee?.employmentSettings?.employmentType;
  const isNonEmployment = isNonEmploymentTypeValue(employmentType);
  const showHoliday = isHolidayTracker && !isNonEmployment;

  const groups = [
    { id: 'personal', label: 'groupPersonal', tiles: ['licno', 'isplata'] },
    {
      id: 'employment',
      label: 'groupEmployment',
      tiles: [
        'ugovor',
        'org',
        'vreme',
        !isNonEmployment && 'primanja',
        // Seen by HLB, the company's salary viewers and the employee.
        showSalary && 'zarada',
        'staz',
        // A member of the HLB team, seen by HLB staff (team assignments).
        showTeam && 'tim',
      ].filter(Boolean),
    },
    showHoliday && {
      id: 'holiday',
      label: 'groupHoliday',
      tiles: ['go', 'odsustva'],
    },
    {
      id: 'dossier',
      label: 'groupDossier',
      tiles: [
        'dok',
        showEquipment && 'oprema',
        // HR of the company and the heads of the sector; never the employee.
        showZaz && 'zaz',
        'istorija',
        'napomene',
      ].filter(Boolean),
    },
  ].filter(Boolean);

  return groups;
}

export const TILE_LABELS = {
  licno: 'tilePersonal',
  isplata: 'tilePayment',
  ugovor: 'tileContract',
  org: 'tileOrg',
  vreme: 'tileWorkTime',
  primanja: 'tileEarnings',
  zarada: 'tileSalary',
  staz: 'tileExperience',
  go: 'tileHoliday',
  odsustva: 'tileLeaves',
  dok: 'tileDocuments',
  istorija: 'tileHistory',
  napomene: 'tileNotes',
  tim: 'tileTeam',
  oprema: 'tileEquipment',
  zaz: 'tileZaz',
};

/**
 * Short summary shown on a tile: { value, hint, warn, alert }.
 * `tp` translates keys of the employeeProfile block.
 */
export function tileSummary(
  id,
  { employee, stats, tp, t, docsCount, team, compensation, equipment, zaz }
) {
  const gd = employee?.generalData || {};
  const es = employee?.employmentSettings || {};
  const api = employee?.additionalPersonalInfo || {};
  const hlc = employee?.holidayLeaveCalculation || {};

  switch (id) {
    case 'licno': {
      const { done, total } = countDossier(employee);
      return {
        value: gd.city || tp('notEntered'),
        hint: [gd.phone, gd.email].filter(Boolean).join(' · '),
        warn: done < total ? tp('missingCount', { count: total - done }) : '',
        info: tp('infoDossier'),
      };
    }
    case 'isplata': {
      const acc = gd.bankAccounts || {};
      // Parts may be stored as "undefined"/"null" text; only digits count.
      const account = [acc.part1, acc.part2, acc.part3]
        .map((part) => String(part ?? '').trim())
        .filter((part) => /^\d+$/.test(part))
        .join('-');
      return {
        value: account || tp('notEntered'),
        hint: [
          gd.bankName,
          gd.isResident === false ? tp('nonResident') : tp('resident'),
        ]
          .filter(Boolean)
          .join(' · '),
      };
    }
    case 'ugovor': {
      const type = es.employmentType;
      const typeLabel =
        type === 'unlimited'
          ? tp('unlimited')
          : type === 'limited'
            ? tp('limited')
            : tp('special');
      const end = es.endDate ? dayjs(es.endDate) : null;
      const daysLeft = end
        ? end.startOf('day').diff(dayjs().startOf('day'), 'day')
        : null;
      return {
        value: typeLabel,
        // End of a fixed-term contract next to the title: that row always
        // shows, while the dates below drop out when the tile is too low.
        // An extension moves endDate, so this is the contract now in force.
        aside:
          type === 'limited' && end
            ? tp('contractUntil', { date: fmt(es.endDate) })
            : '',
        // First day at this employer, under the end date on the value row,
        // which always shows. An extension keeps startDate; a rehire after a
        // gap starts a new profile, so the server takes the earliest start
        // across the profiles it replaced (firstEmploymentDate).
        aside2:
          employee?.firstEmploymentDate || es.startDate
            ? tp('firstEmployment')
            : '',
        aside2Date: fmt(employee?.firstEmploymentDate || es.startDate),
        hint: [fmt(es.startDate), fmt(es.endDate)].filter(Boolean).join(' – '),
        alert:
          end && daysLeft < 0 ? tp('expiredAgo', { count: -daysLeft }) : '',
        warn:
          end && daysLeft >= 0 && daysLeft <= 30
            ? tp('expiresIn', { count: daysLeft })
            : '',
      };
    }
    case 'org': {
      const supervisors = supervisorNames(employee);
      return {
        value:
          employee?.employeeSector?.sectorName ||
          gd.orgUnit ||
          employee?.company?.name ||
          '—',
        hint: gd.position || '',
        hint2: supervisors ? `${tp('supervisor')}: ${supervisors}` : '',
      };
    }
    case 'vreme': {
      if (isNonEmploymentTypeValue(es.employmentType)) {
        return { value: tp('special'), hint: '' };
      }
      const pct =
        es.workTimeType === 'fullTime'
          ? 100
          : es.workTimeType === 'halfTime'
            ? 50
            : es.workTimeCustomType;
      return {
        value: `${pct ?? '—'}%`,
        hint: es.shiftWorker ? tp('shiftWorker') : tp('noShiftWork'),
      };
    }
    case 'primanja': {
      const allowances = employee?.allowances || {};
      const set = [
        'mealAllowance',
        'holidayAllowance',
        'travelAllowance',
        'workFromHomeAllowance',
      ].filter((k) => Number(allowances[k]) > 0);
      return {
        value: tp('allowancesCount', { count: set.length }),
        hint: set
          .map((k) => `${t(k, { ns: 'forms' })} ${allowances[k]}`)
          .join(' · '),
        info: tp('infoEarningsTile'),
      };
    }
    case 'zarada': {
      if (compensation?.salaryHidden) {
        return { value: t('compensation.notAvailable', { ns: 'translations' }), hint: '' };
      }
      const salary = compensation?.salary;
      const pct = salary?.workTimePercent || 100;
      const last = compensation?.payslips?.[0];
      return {
        value: salary
          ? `${Math.round((salary.amount * pct) / 100).toLocaleString('sr-RS')} ${salary.currency}`
          : tp('notEntered'),
        hint: last
          ? tp('salaryLastPayslip', { month: `${String(last.month).padStart(2, '0')}.${last.year}` })
          : '',
      };
    }
    case 'tim':
      return {
        value: tp('teamFirms', { count: team?.firms ?? 0 }),
        hint: team?.confirmed
          ? t('confirm.state_CONFIRMED', { ns: 'teamAssignments' })
          : '',
      };
    case 'staz': {
      // Large: the time in this company. Small: the total, only when
      // experience from before joining is entered (otherwise it is the same
      // number twice), and the start date while work has not begun yet.
      const exp = workExperienceTotal(
        api.workExperience,
        api.workExperienceStartDate,
        es.endDate,
        es.startDate
      );
      const start = exp?.employmentStart;
      const notStarted = !!start && start.isAfter(dayjs().startOf('day'));
      const employments = tp('previousEmploymentsCount', {
        count: api.previousEmployments?.length || 0,
      });
      const extra = [
        notStarted && tp('experienceUi.startsShort', { date: fmt(start) }),
        exp?.hasEntered &&
          tp('experienceUi.totalShort', { span: formatSpan(exp.total, t) }),
      ]
        .filter(Boolean)
        .join(' · ');
      let value = tp('notEntered');
      if (exp?.inFirm) value = formatSpan(exp.inFirm, t);
      else if (notStarted)
        value = formatSpan({ years: 0, months: 0, days: 0 }, t);
      return {
        value,
        hint: extra || employments,
        hint2: extra ? employments : '',
      };
    }
    case 'go': {
      // The balance's bottom line, as on the tile's panel: for a contract
      // ending this year, what is left until it ends.
      const byEnd = hlc.numberOfHolidayDaysByEndOfContract;
      return {
        value: tp('holidayLeft', {
          count: byEnd ?? hlc.numberOfHolidayDays ?? '—',
        }),
        hint:
          byEnd != null
            ? tp('holidayLeftContractEnd')
            : tp('holidayContract', {
                count: hlc.numberOfHolidayDaysContract ?? '—',
              }),
        info: t('totalHolidayDaysLeftYearEnd', { ns: 'messages' }),
      };
    }
    case 'odsustva': {
      const other = (stats?.byType || []).filter(
        (x) => x.key !== 'holidayLeave'
      );
      // Annual leave by the decisions, as on the tile's panel.
      const days =
        other.reduce((s, x) => s + x.totalDays, 0) +
        (stats?.holidayRequestDays || 0);
      return {
        value: tp('leavesUi.workDaysCount', { count: days }),
        hint: String(stats?.year || dayjs().year()),
        info: tp('infoLeavesTile'),
      };
    }
    case 'dok':
      return {
        value: tp('documentsCount', { count: docsCount ?? 0 }),
        hint: tp('attachmentsCount', {
          count: employee?.attachments?.length ?? 0,
        }),
        info: tp('infoDocumentsTile'),
      };
    case 'istorija':
      return {
        value: fmt(employee?.updatedAt) || '—',
        hint: `${tp('createdAt')} ${fmt(employee?.createdAt) || '—'}`,
        info: tp('infoHistoryTile'),
      };
    case 'napomene':
      return {
        value: gd.otherRemarks ? tp('entered') : tp('notEntered'),
        hint: employee?.allowances?.defaultTimesheetComment || '',
      };
    case 'oprema': {
      const requests = equipment?.requests || [];
      return {
        value: t('equipmentRequestsCount', {
          ns: 'employeeNotices',
          count: requests.length,
        }),
        hint: requests[0]
          ? t('lastEquipmentRequest', {
              ns: 'employeeNotices',
              date: fmt(requests[0].requestedAt),
            })
          : '',
      };
    }
    case 'zaz': {
      const latest = zaz?.items?.[0];
      if (!latest) return { value: t('notSent', { ns: 'zaz' }), hint: '' };
      const when = latest.returnedAt && latest.status === 'returned'
        ? latest.returnedAt
        : latest.lastSentAt || latest.sentAt;
      return {
        value: t(`status.${latest.status}`, { ns: 'zaz' }),
        hint: fmt(when) || '',
        warn: latest.status === 'sent' && zaz?.isHead ? t('waitingForHead', { ns: 'zaz' }) : '',
      };
    }
    default:
      return { value: '', hint: '' };
  }
}
